import {
  Upload,
  Card,
  message,
  Typography,
  Modal,
  Divider,
  Col,
  Tag,
  Row,
  Input,
  Button,
  DatePicker,
} from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import {
  UploadOutlined,
  FilePdfOutlined,
  MinusOutlined,
  CheckCircleTwoTone,
  IdcardOutlined,
  SaveOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import axios from "../../services/axiosInstance";
import {
  DELETE_DOCUMENT,
  UPDATE_DOCUMENT,
  UPLOAD_IMAGE,
  UPLOAD_PDF,
} from "../../services/apiEndPoints";
import FullscreenLoader from "./FullPageLoader";
import { imageInfo, snakeToCapitalCase } from "./DualUpload";
import moment, { Moment } from "moment";

const { Text, Title } = Typography;

const SmartFileUpload: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [processedData, setProcessedData] = useState<any>(null);
  const [dragActive, setDragActive] = useState(false);

  // ✅ New editable fields
  const [expiryDate, setExpiryDate] = useState<Moment | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState("");

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";

    if (!isImage && !isPdf) {
      message.error("Only images or PDF files are allowed");
      return Upload.LIST_IGNORE;
    }

    const sizeLimit = isImage ? 5 : 10;
    if (file.size / 1024 / 1024 > sizeLimit) {
      message.error(`File must be smaller than ${sizeLimit}MB`);
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleChange: UploadProps["onChange"] = async ({ fileList }) => {
    const file = fileList[0];
    setFileList(fileList);
    setProcessedData(null);

    if (!file?.originFileObj || !file.type) return;

    setUploading(true);

    try {
      const formData = new FormData();
      let endpoint = "";

      if (file.type.startsWith("image/")) {
        endpoint = UPLOAD_IMAGE;
        formData.append("image", file.originFileObj);
      } else {
        endpoint = UPLOAD_PDF;
        formData.append("file", file.originFileObj, file.name);
      }

      const result = await axios.post(endpoint, formData);
      const data = result?.data?.data;

      setProcessedData(data);
      setExpiryDate(data?.expiry_date ? moment(data.expiry_date) : null);
      setLocation(data?.location || "");
      setCompanyName(data?.companyName || "");

      if (result && result.data && result.data.message) {
        message.error(result.data.message);
        return;
      }

      message.success("File processed successfully");
    } catch {
      message.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (file: UploadFile) => {
    if (file.type?.startsWith("image/")) {
      const reader = new FileReader();
      reader.readAsDataURL(file.originFileObj as Blob);
      reader.onload = () => setPreview(reader.result as string);
    }
  };

  const handleSave = async () => {
    const payload = {
      companyName,
      location,
      expiryDate: expiryDate ? expiryDate.format("YYYY-MM-DD") : null,
    };

    console.log("Payload to be sent:", processedData);

    if (
      processedData &&
      processedData.documentType &&
      processedData.documentType.includes("FSSAI") &&
      !companyName
    ) {
      message.error("Company Name is required for FSSAI documents");
      return;
    }

    const res = await axios.put(
      UPDATE_DOCUMENT(processedData.documentId),
      payload
    );

    window.location.reload();

    message.success("Details saved successfully");
  };

  const handleDelete = async () => {
    if (!processedData?.documentId) return;

    Modal.confirm({
      title: "Delete Document",
      content: "Are you sure you want to delete this document?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(DELETE_DOCUMENT(processedData?.documentId));

          message.success("Document deleted successfully");

          // reset state
          setFileList([]);
          setProcessedData(null);
          setExpiryDate(null);
          setCompanyName("");
          setLocation("");
        } catch {
          message.error("Failed to delete document");
        }
      },
    });
  };

  return (
    <>
      <FullscreenLoader visible={uploading} text="Uploading & Processing..." />

      <Row>
        <Col span={8} />
        <Col span={8}>
          <Upload
            accept="image/*,application/pdf"
            maxCount={1}
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleChange}
            onPreview={handlePreview}
            onRemove={() => setFileList([])}
            showUploadList={false}
            onDrop={() => setDragActive(false)}
          >
            <div
              style={{
                width: "100%",
                height: 240,
                minWidth: 300,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed",
                borderColor: dragActive ? "#1677ff" : "#d9d9d9",
                borderRadius: 16,
                background: dragActive ? "rgba(22,119,255,0.06)" : "#fafafa",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
            >
              {fileList[0]?.type === "application/pdf" ? (
                <FilePdfOutlined style={{ fontSize: 52 }} />
              ) : (
                <UploadOutlined style={{ fontSize: 52 }} />
              )}

              <Text style={{ marginTop: 14, fontSize: 16, fontWeight: 500 }}>
                Tap file to upload
              </Text>
              <Text type="secondary">Image (5MB) • PDF (10MB)</Text>
            </div>
          </Upload>
        </Col>
      </Row>

      {processedData && (
        <Row>
          <Col span={4} />
          <Col span={16} style={{ marginTop: 24 }}>
            <Divider />
            <Title level={4}>Upload Details</Title>

            <Card
              size="small"
              style={{
                borderRadius: 16,
                background: "linear-gradient(135deg, #141e30, #243b55)",
                color: "#fff",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <CheckCircleTwoTone twoToneColor="#52c41a" />
                <Text strong style={{ color: "#fff", marginLeft: 8 }}>
                  Document Detected
                </Text>

                <div style={{ marginTop: 12 }}>
                  <Tag icon={<IdcardOutlined />} color="geekblue">
                    <Text style={{ fontWeight: 700 }}>
                      {processedData.document_type}
                    </Text>
                  </Tag>
                </div>
              </div>

              <Divider style={{ borderColor: "rgba(255,255,255,0.2)" }} />

              {imageInfo(processedData).map((item, index) => (
                <div key={index} style={{ display: "flex", gap: 8 }}>
                  <MinusOutlined />
                  <Text style={{ color: "#fff" }}>
                    <strong>{snakeToCapitalCase(item.key)}</strong> :{" "}
                    {item.value}
                  </Text>
                </div>
              ))}

              <Divider style={{ borderColor: "rgba(255,255,255,0.2)" }} />

              {/* ✅ Editable Fields */}
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Text style={{ color: "#fff" }}>Expiry Date</Text>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={expiryDate}
                    onChange={setExpiryDate}
                  />
                </Col>

                <Col span={8}>
                  <Text style={{ color: "#fff" }}>Company Name</Text>
                  <Input
                    placeholder="Enter company name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </Col>

                <Col span={8}>
                  <Text style={{ color: "#fff" }}>Location</Text>
                  <Input
                    placeholder="Enter location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </Col>
              </Row>

              <Divider style={{ borderColor: "rgba(255,255,255,0.2)" }} />

              <div
                style={{
                  textAlign: "center",
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                }}
              >
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                >
                  Save Changes
                </Button>

                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  Delete
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        open={!!preview}
        footer={null}
        onCancel={() => setPreview(null)}
        width="100vw"
        style={{ top: 0 }}
      >
        <img
          src={preview || ""}
          alt="preview"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Modal>
    </>
  );
};

export default SmartFileUpload;
